import { Box, Button, Divider, LinearProgress, Switch, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { normalizePrescriptionViewSettings } from 'src/clinic/reducers/prescription/utils/prescriptionViewSettingsDefaults'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams, getMPDataWithParams, postMPDataWithParams } from 'src/mentor/services'

const FINAL_REPORT_VIEW_GET = '/api/secure/practitioner/emr-settings/settings/final-report-view'
const FINAL_REPORT_VIEW_SAVE = '/api/secure/practitioner/emr-settings/settings/final-report-view/save'

export default function SupportOnlyDoctorSettings({ practitionerId, mentorProfileData }) {
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [prescriptionViewSettings, setPrescriptionViewSettings] = useState(() =>
    normalizePrescriptionViewSettings(),
  )
  const [saveLoading, setSaveLoading] = useState(false)
  const [emrReportTypeId, setEmrReportTypeId] = useState(null)

  const getEmrReportTypeId = useCallback(async () => {
    try {
      const resp = await getMPDataWithoutParams('/api/health/emr-report-types')
      if (resp?.status === 200 && resp?.data?.length > 0) {
        return resp.data[0].id
      }
    } catch (e) {
      console.error('SupportOnlyDoctorSettings: failed to fetch emr-report-types', e)
    }
    return null
  }, [])

  async function getPrescriptionViewSettings(reportTypeId) {
    const reportType = reportTypeId ?? emrReportTypeId ?? (await getEmrReportTypeId())
    if (!reportType) {
      return
    }
    const params = { 'emr-based-report-type': reportType }
    const response = await getMPDataWithParams(FINAL_REPORT_VIEW_GET, params)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      const fetched = response?.data?.data?.prescriptionViewSettings
      if (fetched) {
        setPrescriptionViewSettings(normalizePrescriptionViewSettings(fetched))
      }
    }
  }

  async function setup() {
    setSettingsLoading(true)
    const id = await getEmrReportTypeId()
    if (id) {
      setEmrReportTypeId(id)
      await getPrescriptionViewSettings(id)
    }
    setSettingsLoading(false)
  }

  useEffect(() => {
    setup()
  }, [])

  async function saveDoctorSettings() {
    const reportType = emrReportTypeId || (await getEmrReportTypeId())
    if (!reportType) {
      toast.error('Could not determine report type for save')
      return
    }
    setSaveLoading(true)
    const params = { 'emr-based-report-type': reportType }
    const response = await postMPDataWithParams(FINAL_REPORT_VIEW_SAVE, prescriptionViewSettings, params)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      const updated = response?.data?.data?.prescriptionViewSettings
      if (updated) {
        setPrescriptionViewSettings(normalizePrescriptionViewSettings(updated))
      }
      toast.success('Settings saved successfully')
    } else {
      toast.error('Failed to save settings')
    }
    setSaveLoading(false)
  }

  const renderPrescriptionSettingRow = (path, label, description = null) => {
    const value = path.split('.').reduce((obj, key) => obj?.[key], prescriptionViewSettings)
    return (
      <div className="d-flex justify-content-between align-items-center py-2">
        <div className="d-flex flex-column gap-1">
          <div className="fw-medium">{label}</div>
          {description && <div className="text-muted small">{description}</div>}
        </div>
        <div className="d-flex align-items-center gap-1">
          <Switch
            id={path.replace(/\./g, '_')}
            checked={!!value}
            onChange={(e) => {
              const newSettings = { ...prescriptionViewSettings }
              const keys = path.split('.')
              const lastKey = keys.pop()
              const target = keys.reduce((obj, key) => {
                if (!obj[key]) obj[key] = {}
                return obj[key]
              }, newSettings)
              target[lastKey] = e.target.checked
              setPrescriptionViewSettings(newSettings)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', maxWidth: '768px', paddingBottom: '80px' }}>
      {settingsLoading && <LinearProgress />}

      {/* Prescription Settings Section */}
      <Box className="mb-4">
        <Typography variant="h6" className="mb-3 fw-bold">
          Prescription Settings
        </Typography>
        {renderPrescriptionSettingRow(
          'showPdfDownloadRelatedButtons',
          'Show PDF Download Buttons',
          'Display PDF download related buttons on prescriptions',
        )}
        {renderPrescriptionSettingRow(
          'showPrescriptionQR',
          'Show Prescription QR Code',
          'Display QR code on prescriptions for easy access',
        )}
      </Box>

      <Divider className="my-4" />

      {/* Print Settings Section */}
      <Box className="mb-4">
        <Typography variant="h6" className="mb-3 fw-bold">
          Print Settings
        </Typography>
        {renderPrescriptionSettingRow(
          'paperPrintSettings.useNewPrintMethod',
          'Use New Paper Print Method',
          'Enable multipage prescription printing method. (This only works for chromium browsers)',
        )}
      </Box>

      <div className="d-flex" style={{ justifyContent: 'flex-end' }}>
        {!settingsLoading && (
          <Button
            onClick={saveDoctorSettings}
            variant="contained"
            disabled={saveLoading || settingsLoading}
          >
            Save {saveLoading && <Spinner animation="border" variant="light" size="sm" />}
          </Button>
        )}
      </div>
    </div>
  )
}
